import React, { useEffect, useMemo, useState } from "react";
import { Dropdown, Loader, Message } from "semantic-ui-react";
import { toast } from "react-toastify";
import { EnvironmentalObjetivesform, Productionform, Site } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useLanguage } from "../../../../contexts";
import { renderTemplate, fillTemplate } from "../../../../utils/renderTemplate";
import { fetchBaselineYearsByAspect, getBaselineYearForKpi } from "../../../../utils/dataAndFormulas/kpiBaselineYear";
import { getRealEnergyKpiValue } from "../../../../utils/dataAndFormulas/energyKpiRealValue";
import { getRealWaterKpiValue } from "../../../../utils/dataAndFormulas/waterKpiRealValue";
import { getRealWasteKpiValue } from "../../../../utils/dataAndFormulas/wasteKpiRealValue";
import { getRealEffluentKpiValue } from "../../../../utils/dataAndFormulas/effluentKpiRealValue";
import { getRealFuelsKpiValue } from "../../../../utils/dataAndFormulas/fuelsKpiRealValue";
import "./ObjectivesMatrix.scss";

// lb_abs (valor absoluto real del KPI en el año base) se lee de acá — mismas
// funciones que usa el formulario para "Valor real actual", pero pidiendo el
// año base del objetivo en vez del año actual. Igual que en el HTML de
// referencia: "EN PRODUCCIÓN: reemplazar KPI_BASELINES ... por fetch() a la
// API de Sustemia".
const REAL_KPI_GETTER_BY_ASPECT = {
  energy: getRealEnergyKpiValue,
  water: getRealWaterKpiValue,
  waste: getRealWasteKpiValue,
  effluent: getRealEffluentKpiValue,
  fuels: getRealFuelsKpiValue,
};

const environmentalObjetivesFormController = new EnvironmentalObjetivesform();
const productionFormController = new Productionform();
const siteController = new Site();

const ASPECT_COLOR = {
  energy: "#1A3562",
  water: "#5E89B4",
  waste: "#A8C4DC",
  effluent: "#7BA3C8",
  fuels: "#2D5F8A",
};

const MIN_HORIZON = 5;

function fmt(n, dec = 0) {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return Number(n).toLocaleString("es-AR", { maximumFractionDigits: dec, minimumFractionDigits: dec });
}

// "Año de reporte" ES el año meta de la proyección (spec v3, sección Guía):
// "El año en que se consolida el resultado. Es el año meta de la proyección
// en la matriz: debe ser posterior al año base."
function getMetaYear(o) {
  return o.year;
}

// Año de línea base de ESTE objetivo puntual. Prioridad:
// 1) o.baselineYear — congelado al crear el objetivo (o al cambiar de KPI):
//    si después cambia la config de ☰ → Datos → Línea Base, este objetivo NO
//    se corre.
// 2) lectura en vivo de esa config — solo para objetivos viejos, creados
//    antes de que existiera este campo, que nunca lo tuvieron guardado.
// 3) año meta − 1, si ninguna de las anteriores está disponible.
function getBaseYearForObjetivo(o, formByAspect) {
  return o.baselineYear ?? getBaselineYearForKpi(o.aspect, o.kpiCode, formByAspect) ?? getMetaYear(o) - 1;
}

// El objetivo se está proyectando con datos estimados/de respaldo en vez de
// la línea base real: o el año base es una suposición (año meta − 1, sin
// configuración real en ☰ → Datos → Línea Base), o el valor absoluto (lb_abs)
// es el derivado del formulario porque todavía no hay dato real cargado para
// ese KPI en ese año.
function isBaselineEstimated(o, formByAspect, lbAbsReal) {
  const hasRealBaseYear = o.baselineYear != null || getBaselineYearForKpi(o.aspect, o.kpiCode, formByAspect) != null;
  return !hasRealBaseYear || lbAbsReal == null;
}

// Proyecta un objetivo año a año, usando el % declarado y el VPS real del
// sitio (si está disponible).
function proyectarObjetivo(o, vpsByYear, years, baseYear, lbAbsReal) {
  const metaYear = getMetaYear(o);
  const nYears = Math.max(1, metaYear - baseYear);
  const pct = Number(o.reductionTons) || 0;
  const impactAbs = Number(o.impact) || 0;

  // lb_abs: valor absoluto real del KPI en el año base (leído de datos reales
  // cargados en Sustemia — ver REAL_KPI_GETTER_BY_ASPECT). Si todavía no hay
  // datos reales para ese año (KPI sin historial cargado), se cae al derivado
  // de "% de reducción" + "impacto absoluto estimado" del formulario, como
  // aproximación provisoria.
  const baselineImplicit = lbAbsReal ?? (pct > 0 ? impactAbs / (pct / 100) : impactAbs);
  const vpsBase = vpsByYear[baseYear] ?? null;
  const intBase = vpsBase ? (baselineImplicit / vpsBase) * 1000 : null;
  const absAtMeta = baselineImplicit * (1 - pct / 100);
  const normAtMeta = intBase !== null ? intBase * (1 - pct / 100) : null;
  const prodGrowth = Number(o.prodGrowth) || 0;

  const proj = years.map((y) => {
    if (y < baseYear) return { year: y, applicable: false };

    const i = y - baseYear;
    const clamp = Math.min(i, nYears);
    const reduction = clamp / nYears;
    const isBase = y === baseYear;
    const isMeta = y === metaYear;

    const absPuro = baselineImplicit - (baselineImplicit - absAtMeta) * reduction;
    const normPlan = intBase !== null ? intBase - (intBase - normAtMeta) * reduction : null;
    // Preferimos el VPS real del año (si ya se cargó producción para ese año)
    // sobre el proyectado por crecimiento estimado — solo cae al proyectado
    // cuando todavía no hay dato real (típicamente años futuros).
    const vpsProjected = vpsBase ? vpsBase * Math.pow(1 + prodGrowth / 100, i) : null;
    const vpsY = vpsByYear[y] ?? vpsProjected;

    // Si la base declarada es "absoluta", el normalizado se DERIVA del camino
    // absoluto (absPuro ÷ VPS del año) en vez de calcularse en una senda lineal
    // independiente. Con producción creciente, la intensidad de un objetivo
    // absoluto cae más rápido que lineal (fix v3, Oráculo 2 del Excel).
    let abs, norm;
    if (o.baseMedicion === "absoluta") {
      abs = absPuro;
      norm = vpsY ? (absPuro / vpsY) * 1000 : null;
    } else {
      norm = normPlan;
      abs = norm !== null && vpsY ? (norm / 1000) * vpsY : absPuro;
    }

    const deltaAbs = isBase ? 0 : ((abs - baselineImplicit) / baselineImplicit) * 100;
    const deltaNorm = isBase || intBase === null || norm === null ? 0 : ((norm - intBase) / intBase) * 100;
    const diverge = !isBase && norm !== null && deltaNorm < -0.5 && deltaAbs > 0.5;

    return { year: y, applicable: true, isBase, isMeta, abs, norm, deltaAbs, deltaNorm, diverge, hasNorm: norm !== null };
  });

  // Acumulado del horizonte (spec v6.2): Σ, sobre los años posteriores a la
  // base, de (LBO_abs − proyección_abs de ese año). Positivo = evitado vs.
  // mantener la línea base constante; negativo = adicional (divergencia por
  // crecimiento de producción) — nunca se oculta.
  const acumulado = proj
    .filter((p) => p.applicable && !p.isBase)
    .reduce((s, p) => s + (baselineImplicit - p.abs), 0);

  return { proj, acumulado };
}

export function ObjectivesMatrix({ siteSelected }) {
  const { accessToken } = useAuth();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const [objetivos, setObjetivos] = useState([]);
  const [vpsByYear, setVpsByYear] = useState({});
  const [formByAspect, setFormByAspect] = useState({});
  const [lbAbsByObjetivo, setLbAbsByObjetivo] = useState({});
  const [loading, setLoading] = useState(false);
  const [vistaBase, setVistaBase] = useState("ambas");
  const [filterAspect, setFilterAspect] = useState("");
  // Fin del horizonte del plan (año) — piso configurable, se persiste en el
  // Site. null hasta que se confirma el fetch (evita "flash" con el default).
  const [horizonYear, setHorizonYear] = useState(null);
  const [savingHorizon, setSavingHorizon] = useState(false);

  const aspectosOptions = [
    { key: "all", text: t("om_filter_all"), value: "" },
    { key: "energy", text: t("energy"), value: "energy" },
    { key: "water", text: t("water"), value: "water" },
    { key: "waste", text: t("waste"), value: "waste" },
    { key: "effluent", text: t("effluent"), value: "effluent" },
    { key: "fuels", text: t("fuels"), value: "fuels" },
  ];

  const toggleOptions = [
    { key: "ambas", text: t("om_toggle_both") },
    { key: "absoluta", text: t("om_toggle_abs") },
    { key: "normalizada", text: t("om_toggle_norm") },
  ];

  useEffect(() => {
    (async () => {
      if (!siteSelected || !accessToken) return;
      setLoading(true);
      try {
        const response = await environmentalObjetivesFormController.getEnvironmentalObjetivesFormsBySite(
          accessToken,
          siteSelected
        );
        const objs = response.code === 200 ? response.objetives || [] : [];
        setObjetivos(objs);

        const siteRes = await siteController.getSite(accessToken, siteSelected).catch(() => null);
        const savedHorizon = siteRes?.horizonYear || null;
        setHorizonYear(savedHorizon);

        // Horizonte real a proyectar (mismo cálculo que el useMemo `years` de
        // abajo, pero acá se recalcula sobre `objs` recién llegado porque el
        // estado `objetivos` todavía no se actualizó dentro de este efecto).
        // Los objetivos sin KPI no se proyectan en la matriz (spec v7).
        const activeObjs = objs.filter((o) => o.active !== false && o.kpiCode);
        const formByAspectResult = await fetchBaselineYearsByAspect(
          accessToken,
          siteSelected,
          activeObjs.map((o) => o.aspect)
        );
        setFormByAspect(formByAspectResult);

        const baseYears = activeObjs.map((o) => getBaseYearForObjetivo(o, formByAspectResult));
        const metaYears = activeObjs.map(getMetaYear);
        const minYear = baseYears.length ? Math.min(...baseYears) : new Date().getFullYear();
        const horizonFloor = savedHorizon || minYear + MIN_HORIZON;
        const maxYear = activeObjs.length ? Math.max(...metaYears, horizonFloor) : horizonFloor;
        const yearsToFetch = Array.from({ length: maxYear - minYear + 1 }, (_, i) => minYear + i);

        // lb_abs real por objetivo: valor absoluto real del KPI vinculado en
        // SU año base (no el estimado a mano en el formulario).
        try {
          const lbAbsEntries = await Promise.all(
            activeObjs.map(async (o) => {
              const getter = REAL_KPI_GETTER_BY_ASPECT[o.aspect];
              if (!getter || !o.kpiCode) return [o._id, null];
              const baseYearForO = getBaseYearForObjetivo(o, formByAspectResult);
              try {
                const res = await getter({ accessToken, site: siteSelected, kpiCode: o.kpiCode, year: baseYearForO });
                return [o._id, res?.available ? res.total : null];
              } catch (error) {
                return [o._id, null];
              }
            })
          );
          setLbAbsByObjetivo(Object.fromEntries(lbAbsEntries));
        } catch (error) {
          setLbAbsByObjetivo({});
        }

        try {
          const entries = await Promise.all(
            yearsToFetch.map((y) =>
              productionFormController
                .getTotalBaselineByYearAndSite(accessToken, siteSelected, y, "standardized_production")
                .then((res) => [y, res?.total ?? res?.value ?? null])
                .catch(() => [y, null])
            )
          );
          setVpsByYear(Object.fromEntries(entries));
        } catch (error) {
          setVpsByYear({});
        }
      } catch (error) {
        console.error(error);
        setObjetivos([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [siteSelected, accessToken]);

  // Los objetivos sin KPI de Sustemia (kpiCode null — objetivos de gestión)
  // no se proyectan acá: no hay línea base numérica que proyectar. Spec v7:
  // "NO aparecen en la matriz ni en acumulado ni en divergencias; SÍ
  // participan de plan, bandas, senda, trayectoria" (eso es Plan Estratégico).
  const objetivosFiltrados = useMemo(
    () => objetivos.filter((o) => o.active !== false && !!o.kpiCode && (!filterAspect || o.aspect === filterAspect)),
    [objetivos, filterAspect]
  );

  const minYear = useMemo(() => {
    if (objetivosFiltrados.length === 0) return null;
    const baseYears = objetivosFiltrados.map((o) => getBaseYearForObjetivo(o, formByAspect));
    return Math.min(...baseYears);
  }, [objetivosFiltrados, formByAspect]);

  const years = useMemo(() => {
    if (objetivosFiltrados.length === 0 || minYear === null) return [];
    const metaYears = objetivosFiltrados.map(getMetaYear);
    const horizonFloor = horizonYear || minYear + MIN_HORIZON;
    const maxYear = Math.max(...metaYears, horizonFloor);
    return Array.from({ length: maxYear - minYear + 1 }, (_, i) => minYear + i);
  }, [objetivosFiltrados, formByAspect, minYear, horizonYear]);

  const horizonOptions = useMemo(() => {
    if (minYear === null) return [];
    const start = minYear + 1;
    const end = minYear + 20;
    return Array.from({ length: end - start + 1 }, (_, i) => {
      const y = start + i;
      return { key: y, value: y, text: String(y) };
    });
  }, [minYear]);

  const handleHorizonChange = async (value) => {
    setHorizonYear(value);
    setSavingHorizon(true);
    try {
      await siteController.updateSite(accessToken, siteSelected, { horizonYear: value });
    } catch (error) {
      console.error(error);
      toast.error(t("om_toast_horizon_error"));
    } finally {
      setSavingHorizon(false);
    }
  };

  const proyecciones = useMemo(
    () => objetivosFiltrados.map((o) => {
      const baseYear = getBaseYearForObjetivo(o, formByAspect);
      const lbAbsReal = lbAbsByObjetivo[o._id];
      const { proj, acumulado } = proyectarObjetivo(o, vpsByYear, years, baseYear, lbAbsReal);
      return {
        o,
        baseYear,
        estimated: isBaselineEstimated(o, formByAspect, lbAbsReal),
        proj,
        acumulado,
      };
    }),
    [objetivosFiltrados, vpsByYear, years, formByAspect, lbAbsByObjetivo]
  );

  // Bandas por aspecto (spec v6.1/v6.6) — mismo patrón que aspectGroups en
  // StrategicPlan.js, orden de primera aparición. La reducción media se
  // calcula DENTRO de cada grupo — nunca promediada entre aspectos distintos.
  const matrixAspectGroups = useMemo(() => {
    const order = [];
    const byAspect = {};
    proyecciones.forEach((p) => {
      const asp = p.o.aspect || "—";
      if (!byAspect[asp]) { byAspect[asp] = []; order.push(asp); }
      byAspect[asp].push(p);
    });
    return order.map((aspect) => {
      const items = byAspect[aspect];
      return {
        aspect,
        items,
        avgReduction: items.reduce((s, { o }) => s + (Number(o.reductionTons) || 0), 0) / items.length,
      };
    });
  }, [proyecciones]);

  const summary = useMemo(() => {
    const total = objetivosFiltrados.length;
    const aspectosCount = new Set(objetivosFiltrados.map((o) => o.aspect)).size;
    const conDivergencia = proyecciones.filter(({ proj }) => proj.some((p) => p.applicable && p.diverge)).length;
    const metaMax = total ? Math.max(...objetivosFiltrados.map(getMetaYear)) : "—";
    return { total, aspectosCount, conDivergencia, metaMax };
  }, [objetivosFiltrados, proyecciones]);

  const showAbs = vistaBase !== "normalizada";
  const showNorm = vistaBase !== "absoluta";

  const exportCSV = () => {
    const header = [
      t("om_csv_header_aspect"), t("om_csv_header_action"), t("om_csv_header_source"),
      t("om_csv_header_pct_meta"), t("om_csv_header_base_year"), t("om_csv_header_meta_year"),
      t("om_csv_header_base_declared"),
    ];
    years.forEach((y) => header.push(`${y}_Abs`, `${y}_Norm`));

    const rows = [header];
    proyecciones.forEach(({ o, proj, baseYear }) => {
      const row = [o.aspect, o.action, o.source, o.reductionTons, baseYear, getMetaYear(o), o.baseMedicion];
      proj.forEach((p) => {
        row.push(p.applicable ? Math.round(p.abs) : "", p.applicable && p.hasNorm ? p.norm.toFixed(2) : "");
      });
      rows.push(row);
    });

    const csv = rows.map((r) => r.map((v) => `"${v ?? ""}"`).join(",")).join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sustemia_matriz_objetivos_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(t("om_toast_csv_exported"));
  };

  if (loading) return <Loader active inline="centered" />;

  return (
    <div className="objectives-matrix">
      {/* ── Encabezado ── */}
      <div className="om-head">
        <div>
          <h1>{t("om_title")}</h1>
          <p>
            {renderTemplate(t("om_subtitle_template"), {
              abs: <strong>{t("om_word_absolute")}</strong>,
              norm: <strong>{t("om_word_normalized")}</strong>,
            })}
          </p>
        </div>
        <div className="om-controls">
          <div className="om-field">
            <label>{t("om_view_base_label")}</label>
            <div className="om-toggle">
              {toggleOptions.map((b) => (
                <button
                  key={b.key}
                  className={vistaBase === b.key ? "active" : ""}
                  onClick={() => setVistaBase(b.key)}
                  type="button"
                >
                  {b.text}
                </button>
              ))}
            </div>
          </div>
          <div className="om-field">
            <label>{t("om_filter_aspect_label")}</label>
            <Dropdown
              selection
              compact
              options={aspectosOptions}
              value={filterAspect}
              onChange={(_, { value }) => setFilterAspect(value)}
            />
          </div>
          <div className="om-field">
            <label>{t("om_horizon_label")}</label>
            <Dropdown
              selection
              compact
              loading={savingHorizon}
              disabled={horizonOptions.length === 0}
              options={horizonOptions}
              value={horizonYear || ""}
              placeholder={t("om_horizon_placeholder")}
              onChange={(_, { value }) => handleHorizonChange(value)}
            />
          </div>
        </div>
      </div>

      {/* ── Banda explicativa ── */}
      <div className="om-explainer">
        <div className="om-explainer__icon">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="8" r="7" stroke="#1A3562" strokeWidth="1.3" />
            <path d="M8 5v3.5M8 11h.01" stroke="#1A3562" strokeWidth="1.3" strokeLinecap="round" />
          </svg>
        </div>
        <div>
          <strong>{t("om_explainer_title")}</strong>{" "}
          {renderTemplate(t("om_explainer_body_template"), {
            abs: <strong>{t("om_word_absolute")}</strong>,
            norm: <strong>{t("om_word_normalized")}</strong>,
            flag: <strong>{t("om_word_divergence_flag")}</strong>,
          })}
        </div>
      </div>

      {/* ── Tarjetas resumen ── */}
      <div className="om-summary">
        <div className="om-sum-card">
          <div className="om-sc-label">{t("om_summary_active_label")}</div>
          <div className="om-sc-value">{summary.total}</div>
          <div className="om-sc-sub">
            {renderTemplate(t("om_summary_active_sub_template"), { count: summary.aspectosCount })}
          </div>
        </div>
        <div className="om-sum-card">
          <div className="om-sc-label">{t("om_summary_reduction_label")}</div>
          <div className="om-sc-value om-sc-value--sm">
            {matrixAspectGroups.length
              ? matrixAspectGroups
                  .map(({ aspect, avgReduction }) => `${t(aspect)} ${avgReduction.toFixed(1).replace(".", ",")}%`)
                  .join(" · ")
              : "—"}
          </div>
          <div className="om-sc-sub">{t("om_summary_reduction_sub")}</div>
        </div>
        <div className="om-sum-card">
          <div className="om-sc-label">{t("om_summary_divergence_label")}</div>
          <div className="om-sc-value">{summary.conDivergencia}</div>
          <div className="om-sc-sub">{summary.conDivergencia ? t("om_summary_divergence_sub_yes") : t("om_summary_divergence_sub_no")}</div>
        </div>
        <div className="om-sum-card om-sum-card--accent">
          <div className="om-sc-label">{t("om_summary_horizon_label")}</div>
          <div className="om-sc-value">{years.length ? `${years[0]}–${years[years.length - 1]}` : "—"}</div>
          <div className="om-sc-sub">
            {renderTemplate(t("om_summary_horizon_sub_template"), { year: summary.metaMax })}
          </div>
        </div>
      </div>

      {/* ── Exportar (arriba de la tabla) ── */}
      <div className="om-table-actions">
        <button type="button" className="om-export-btn" onClick={exportCSV} disabled={objetivosFiltrados.length === 0}>
          {t("om_export_csv")}
        </button>
      </div>

      {objetivosFiltrados.length === 0 ? (
        <Message>
          <Message.Header>{t("om_empty_title")}</Message.Header>
          <p>{t("om_empty_body")}</p>
        </Message>
      ) : (
        <div className="om-table-wrap">
          <div className="om-table-scroll">
            <table className="om-table">
              <thead>
                <tr>
                  <th rowSpan="2" className="om-th-obj">{t("om_th_objective")}</th>
                  {years.map((y) => (
                    <th key={y} colSpan={(showAbs ? 1 : 0) + (showNorm ? 1 : 0)} className="om-yr-group">
                      {y}
                      <span className="om-yr-tag">{y === years[0] ? t("om_th_baseline_tag") : renderTemplate(t("om_th_year_tag_template"), { n: y - years[0] })}</span>
                    </th>
                  ))}
                  <th rowSpan="2">{t("om_th_reduction")}<span className="om-yr-tag">{t("om_th_reduction_sub")}</span></th>
                  <th rowSpan="2" title={t("om_th_accumulated_sub")}>{t("om_th_accumulated")}<span className="om-yr-tag">{t("om_th_accumulated_sub")}</span></th>
                </tr>
                <tr className="om-subhead-row">
                  {years.map((y) => (
                    <React.Fragment key={y}>
                      {showAbs && <th className="om-subhead">{t("om_th_abs")}</th>}
                      {showNorm && <th className="om-subhead">{t("om_th_norm")}</th>}
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {matrixAspectGroups.flatMap(({ aspect, items, avgReduction }) => [
                  <tr className="om-asp-band" key={`band-${aspect}`}>
                    <td
                      colSpan={1 + years.length * ((showAbs ? 1 : 0) + (showNorm ? 1 : 0)) + 2}
                      style={{ borderLeft: `4px solid ${ASPECT_COLOR[aspect] || "#5E89B4"}` }}
                    >
                      <span className="om-aspect-dot" style={{ background: ASPECT_COLOR[aspect] || "#5E89B4", marginRight: 7 }} />
                      {t(aspect)} · {fillTemplate(t("om_asp_band_template"), { count: items.length })}
                      {" · "}
                      <span className="om-asp-band__av">
                        {fillTemplate(t("om_asp_band_avg_template"), { pct: avgReduction.toFixed(1).replace(".", ",") })}
                      </span>
                    </td>
                  </tr>,
                  ...items.map(({ o, baseYear, estimated, proj, acumulado }) => {
                  const color = ASPECT_COLOR[o.aspect] || "#5E89B4";
                  return (
                    <tr key={o._id}>
                      <td className="om-obj-cell">
                        <div className="om-obj-title">
                          <span className="om-aspect-dot" style={{ background: color }} />
                          <span className="om-kpi-code">{o.code || o.kpiCode || (o.aspect || "").slice(0, 3).toUpperCase()}</span>
                          {t(o.action)} {o.source}
                        </div>
                        <div className="om-obj-meta">
                          {renderTemplate(t("om_obj_meta_template"), {
                            aspect: t(o.aspect),
                            pct: o.reductionTons,
                            baseYear,
                            year: getMetaYear(o),
                            base: <strong>{t(o.baseMedicion)}</strong>,
                          })}
                        </div>
                        {o.originalYear && (
                          <div className="om-extended-badge">
                            {fillTemplate(t("om_extended_badge_template"), { originalYear: o.originalYear, year: getMetaYear(o) })}
                          </div>
                        )}
                        {estimated && (
                          <div className="om-estimated-badge">{t("om_estimated_baseline_badge")}</div>
                        )}
                      </td>
                      {proj.map((p) => (
                        <React.Fragment key={p.year}>
                          {showAbs && (
                            <td className={`om-abs-col ${p.isBase ? "om-cell-baseline" : p.isMeta ? "om-cell-target" : ""}`}>
                              {p.applicable ? (
                                <>
                                  <span className="om-val-main">{fmt(p.abs)}</span>
                                  {!p.isBase && (
                                    <span className={`om-val-delta ${p.deltaAbs < 0 ? "down" : p.deltaAbs > 0 ? "up" : ""}`}>
                                      {p.deltaAbs > 0 ? "+" : ""}{p.deltaAbs.toFixed(1)}%
                                    </span>
                                  )}
                                  {p.diverge && <span className="om-diverge-flag">▲ vol</span>}
                                </>
                              ) : "—"}
                            </td>
                          )}
                          {showNorm && (
                            <td className={p.isBase ? "om-cell-baseline" : p.isMeta ? "om-cell-target" : ""}>
                              {p.applicable && p.hasNorm ? (
                                <>
                                  <span className="om-val-norm">{fmt(p.norm, 1)}</span>
                                  {!p.isBase && (
                                    <span className={`om-val-delta ${p.deltaNorm < 0 ? "down" : p.deltaNorm > 0 ? "up" : ""}`}>
                                      {p.deltaNorm > 0 ? "+" : ""}{p.deltaNorm.toFixed(1)}%
                                    </span>
                                  )}
                                </>
                              ) : p.applicable ? (
                                <span className="om-val-delta">{t("om_nodata_vps")}</span>
                              ) : "—"}
                            </td>
                          )}
                        </React.Fragment>
                      ))}
                      <td>
                        <span className="om-val-main down">{o.reductionTons}%</span>
                        <span className="om-val-delta down">↓ {t(o.aspect)}</span>
                      </td>
                      <td className={acumulado < 0 ? "om-cell-additional" : "om-cell-avoided"}>
                        {acumulado === 0 ? (
                          "—"
                        ) : acumulado > 0 ? (
                          fillTemplate(t("om_accumulated_avoided_template"), { value: fmt(acumulado) })
                        ) : (
                          fillTemplate(t("om_accumulated_additional_template"), { value: fmt(Math.abs(acumulado)) })
                        )}
                      </td>
                    </tr>
                  );
                }),
                ])}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="om-footer">
        <div className="om-legend">
          <span><span className="om-legend-dot om-legend-base" /> {t("om_legend_baseline")}</span>
          <span><span className="om-legend-dot om-legend-down" /> {t("om_legend_reduction")}</span>
          <span><span className="om-legend-dot om-legend-target" /> {t("om_legend_target")}</span>
          <span><span className="om-legend-dot om-legend-diverge" /> {t("om_legend_divergence")}</span>
        </div>
      </div>
    </div>
  );
}
